import React, { useState, useEffect, useRef } from 'react';
import { db, storage } from '../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import emailjs from '@emailjs/browser';
import './AlbumGrid.css';

// Initialize EmailJS with your public key
emailjs.init('Pho65QjOyrZIbrkYQ');

// Email notification function - DISABLED (uncomment the call in handleShuffleAll to enable)
const sendShuffleNotification = () => {
  emailjs.send(
    'service_swe4vib',
    'template_tuctnw2',
    {
      to_email: 'mvc757@gmail.com',
      message: 'Hello Mario!',
      from_name: 'Jookbox'
    }
  ).then(
    (result) => console.log('📧 Email sent!', result.text),
    (error) => console.log('📧 Email failed:', error.text)
  );
};

// Individual Album Card with LAZY image loading using IntersectionObserver
function AlbumCard({ album, isSelected, isInPlaylist, isQueued, onSelect }) {
  const [coverImageUrl, setCoverImageUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const cardRef = useRef(null);

  // IntersectionObserver for lazy loading - only load when visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        rootMargin: '100px', // Start loading 100px before entering viewport
        threshold: 0.1
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
      observer.disconnect();
    };
  }, []);

  // Load image only when visible and not already loaded
  useEffect(() => {
    if (!isVisible || hasLoaded) return;

    let isMounted = true;

    const loadCoverImage = async () => {
      if (!album.coverUrl) {
        if (isMounted) {
          setLoadingImage(false);
          setHasLoaded(true);
        }
        return;
      }

      // Skip loading if album is marked as unavailable to avoid permission errors
      if (album.storageStatus === 'unavailable') {
        if (isMounted) {
          setCoverImageUrl(null);
          setLoadingImage(false);
          setHasLoaded(true);
        }
        return;
      }

      if (isMounted) setLoadingImage(true);

      try {
        let storagePath = album.coverUrl;
        if (storagePath.startsWith('/')) {
          storagePath = storagePath.substring(1);
        }
        
        // Prepend the type (Albums or Singles) if not already in the path
        const type = album.type || 'Albums';
        if (!storagePath.startsWith(type + '/')) {
          storagePath = `${type}/${storagePath}`;
        }
        
        const imageRef = ref(storage, storagePath);
        const url = await getDownloadURL(imageRef);
        
        if (isMounted) {
          setCoverImageUrl(url);
          setHasLoaded(true);
        }
      } catch (error) {
        if (isMounted) {
          setCoverImageUrl(null);
          setHasLoaded(true);
        }
      } finally {
        if (isMounted) {
          setLoadingImage(false);
        }
      }
    };

    loadCoverImage();

    return () => {
      isMounted = false;
    };
  }, [isVisible, hasLoaded, album.coverUrl, album.storageStatus, album.type]);

  return (
    <div 
      ref={cardRef}
      className={`album-grid-card ${isSelected ? 'selected' : ''} ${isInPlaylist ? 'in-playlist' : ''} ${isQueued ? 'queued' : ''} ${album.storageStatus === 'unavailable' ? 'unavailable' : ''}`}
      onClick={(e) => onSelect(album, e)}
      title={album.storageStatus === 'unavailable' ? 'Files not available in storage' : ''}
    >
      <div className="album-grid-cover">
        {!isVisible || loadingImage ? (
          <div className="album-grid-placeholder">
            <span>{loadingImage ? '⏳' : '🎵'}</span>
          </div>
        ) : coverImageUrl ? (
          <img 
            src={coverImageUrl} 
            alt={album.album}
            loading="lazy"
            onError={(e) => { 
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = '<div class="album-grid-placeholder"><span>🎵</span></div>';
            }}
          />
        ) : (
          <div className="album-grid-placeholder">
            <span>🎵</span>
          </div>
        )}
        {isSelected && <div className="album-grid-selected-overlay">✓ Playing</div>}
        {isInPlaylist && <div className="album-grid-playlist-badge">📋</div>}
        {isQueued && !isSelected && <div className="album-grid-queued-badge">🎵</div>}
        {album.storageStatus === 'unavailable' && (
          <div className="album-grid-unavailable-badge" style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            background: 'rgba(255, 152, 0, 0.9)',
            color: '#fff',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            zIndex: 2
          }}>⚠️ No Files</div>
        )}
      </div>
      <div className="album-grid-info">
        <h4 className="album-grid-title">{album.album || 'Unknown Album'}</h4>
        <p className="album-grid-artist">{album.artist || 'Unknown Artist'}</p>
      </div>
    </div>
  );
}

function AlbumGrid({ selectedPath, selectedAlbum, onAlbumSelect, hierarchy, onShufflePlay, queuedAlbumIds = new Set(), onAddToQueue, onQueueModeChange, onQueryMusicbrainz, onQuerySpotify, onQueryLastfm, hideUtilityRow = false, mediaPlayerVisible = false, onDoneSearching, selectedArtists = [], isFilteringByArtists = false }) {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    decades: [],
    artists: []
  });
  const [activeFilters, setActiveFilters] = useState({
    decade: 'all',
    artist: 'all'
  });
  const [searchQuery, setSearchQuery] = useState('');
  // Search field starts as icon-only, expands when clicked
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [selectedAlbums, setSelectedAlbums] = useState(new Set());
  const [isShiftMode, setIsShiftMode] = useState(false);
  const [sortMode, setSortMode] = useState('album'); // 'album' or 'artist'
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [showQueuedOnly, setShowQueuedOnly] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'artist'
  const [expandedArtists, setExpandedArtists] = useState(new Set());
  const [isQueueMode, setIsQueueMode] = useState(false); // Queue mode toggle
  const [focusedAlbumIndex, setFocusedAlbumIndex] = useState(-1); // Gamepad navigation
  const [focusedToolbarIndex, setFocusedToolbarIndex] = useState(-1); // Toolbar navigation
  const gridRef = useRef(null);
  const toolbarRef = useRef(null);

  // Helper function to normalize artist names (remove numbers/suffixes for matching)
  const normalizeArtistName = (artistName) => {
    if (!artistName) return '';
    return artistName.replace(/\s+\d+$/, '').trim();
  };

  // Helper function to check if an artist matches any in the selectedArtists array
  const artistMatches = (artistName) => {
    if (!isFilteringByArtists || selectedArtists.length === 0) return true;
    const normalized = normalizeArtistName(artistName);
    return selectedArtists.some(selected => 
      normalizeArtistName(selected) === normalized
    );
  };

  // Load albums from hierarchy based on selected path
  useEffect(() => {
    setAlbums([]);
    
    // Need at least a type selected to show albums
    if (!selectedPath.type || !hierarchy || !hierarchy.albums) {
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Extract all albums from hierarchy that match the current path
    const matchingAlbums = [];
    const artistsSet = new Set();
    const decadesSet = new Set();
    
    console.log('🔍 AlbumGrid: Loading albums for path:', selectedPath);
    console.log('🔍 AlbumGrid: Type filter:', selectedPath.type);
    console.log('🔍 AlbumGrid: Hierarchy artist keys:', Object.keys(hierarchy.albums).length);
    
    // Iterate through all albums in the hierarchy
    let matchedKeys = 0;
    Object.keys(hierarchy.albums).forEach(artistKey => {
      // artistKey format: "language|genre|subgenre|artist"
      const [lang, genre, subgenre, artist] = artistKey.split('|');
      
      // Check if this artist's albums match the current selection
      // If only type is selected, match all languages/genres/subgenres/artists
      const matches = 
        (!selectedPath.language || lang === selectedPath.language) &&
        (!selectedPath.genre || genre === selectedPath.genre) &&
        (!selectedPath.subgenre || subgenre === selectedPath.subgenre) &&
        (!selectedPath.artist || artist === selectedPath.artist);
      
      // Only filter by selectedArtists if "Done Searching" has been clicked
      const artistMatchesFilter = artistMatches(artist);
      
      if (matches && artistMatchesFilter) {
        matchedKeys++;
        // Add all albums from this artist that match the type
        hierarchy.albums[artistKey].forEach(album => {
          // IMPORTANT: Filter by type (Albums vs Singles)
          if (selectedPath.type && album.type !== selectedPath.type) {
            return; // Skip albums that don't match the selected type
          }
          
          // Additional check: if filtering is enabled and selectedArtists is provided, verify the album's artist matches
          if (isFilteringByArtists && selectedArtists.length > 0) {
            const albumArtist = album.artist || artist;
            if (!artistMatches(albumArtist)) {
              return; // Skip albums that don't match selected artists
            }
          }
          
          matchingAlbums.push(album);
          if (album.artist) artistsSet.add(album.artist);
          
          // Extract decade
          const yearMatch = album.album?.match(/\[(\d{4})\]/);
          if (yearMatch) {
            const decade = Math.floor(parseInt(yearMatch[1]) / 10) * 10;
            decadesSet.add(`${decade}s`);
          }
        });
      }
    });
    
    const languageFilter = selectedPath.language ? `for language: ${selectedPath.language}` : 'for all languages';
    console.log(`🔍 AlbumGrid: Matched ${matchedKeys} artist keys ${languageFilter}`);
    
    const unavailableCount = matchingAlbums.filter(a => a.storageStatus === 'unavailable').length;
    const availableCount = matchingAlbums.length - unavailableCount;
    
    // Show language distribution
    const languageCounts = {};
    matchingAlbums.forEach(album => {
      const lang = album.language || 'Unknown';
      languageCounts[lang] = (languageCounts[lang] || 0) + 1;
    });
    
    console.log(`🔍 AlbumGrid: Found ${matchingAlbums.length} matching albums`);
    console.log(`   ✅ Available: ${availableCount}`);
    console.log(`   ⚠️ Unavailable: ${unavailableCount}`);
    console.log(`   🌐 Languages found:`, languageCounts);
    
    setAlbums(matchingAlbums);
    setFilters({
      decades: Array.from(decadesSet).sort(),
      artists: Array.from(artistsSet).sort()
    });
    setLoading(false);
  }, [selectedPath, hierarchy, selectedArtists, isFilteringByArtists]);

  // Handle shift key tracking
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Shift') {
        setIsShiftMode(true);
      }
    };
    
    const handleKeyUp = (e) => {
      if (e.key === 'Shift') {
        setIsShiftMode(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Gamepad album navigation with right joystick
  useEffect(() => {
    const handleAlbumNavigate = (e) => {
      const { direction } = e.detail;
      
      // If navigating UP from toolbar, go to albums
      if (direction === 'down' && focusedToolbarIndex >= 0) {
        // Clear toolbar focus
        const toolbarButtons = toolbarRef.current?.querySelectorAll('.utility-btn');
        toolbarButtons?.forEach(btn => btn.classList.remove('gamepad-focused'));
        setFocusedToolbarIndex(-1);
        // Focus first album
        const albumCards = gridRef.current?.querySelectorAll('.album-grid-card');
        if (albumCards && albumCards.length > 0) {
          albumCards[0].classList.add('gamepad-focused');
          albumCards[0].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          setFocusedAlbumIndex(0);
        }
        return;
      }
      
      // If navigating LEFT/RIGHT in toolbar
      if ((direction === 'left' || direction === 'right') && focusedToolbarIndex >= 0) {
        const toolbarButtons = toolbarRef.current?.querySelectorAll('.utility-btn:not(:disabled)');
        if (!toolbarButtons || toolbarButtons.length === 0) return;
        
        toolbarButtons.forEach(btn => btn.classList.remove('gamepad-focused'));
        
        let newIndex = focusedToolbarIndex;
        if (direction === 'left') {
          newIndex = newIndex > 0 ? newIndex - 1 : toolbarButtons.length - 1;
        } else {
          newIndex = newIndex < toolbarButtons.length - 1 ? newIndex + 1 : 0;
        }
        
        toolbarButtons[newIndex].classList.add('gamepad-focused');
        setFocusedToolbarIndex(newIndex);
        console.log('🎮 Toolbar focus:', newIndex);
        return;
      }
      
      const albumCards = gridRef.current?.querySelectorAll('.album-grid-card');
      if (!albumCards || albumCards.length === 0) return;
      
      // Calculate grid columns (approximate based on card width)
      const gridWidth = gridRef.current?.offsetWidth || 800;
      const cardWidth = 180; // Approximate card width including gap
      const columns = Math.max(1, Math.floor(gridWidth / cardWidth));
      
      // Remove focus from all
      albumCards.forEach(card => card.classList.remove('gamepad-focused'));
      
      let newIndex = focusedAlbumIndex;
      
      if (direction === 'up') {
        if (newIndex < columns) {
          // At top row - jump to toolbar
          albumCards.forEach(card => card.classList.remove('gamepad-focused'));
          setFocusedAlbumIndex(-1);
          
          const toolbarButtons = toolbarRef.current?.querySelectorAll('.utility-btn:not(:disabled)');
          if (toolbarButtons && toolbarButtons.length > 0) {
            toolbarButtons[0].classList.add('gamepad-focused');
            setFocusedToolbarIndex(0);
            console.log('🎮 Jumped to toolbar');
          }
          return;
        }
        newIndex = newIndex - columns;
      } else if (direction === 'down') {
        newIndex = newIndex + columns < albumCards.length ? newIndex + columns : newIndex;
      } else if (direction === 'left') {
        newIndex = newIndex > 0 ? newIndex - 1 : albumCards.length - 1;
      } else if (direction === 'right') {
        newIndex = newIndex < albumCards.length - 1 ? newIndex + 1 : 0;
      }
      
      // Default to first album if none focused
      if (newIndex < 0) newIndex = 0;
      
      // Apply focus
      if (albumCards[newIndex]) {
        albumCards[newIndex].classList.add('gamepad-focused');
        albumCards[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setFocusedAlbumIndex(newIndex);
        console.log('🎮 Album focus:', newIndex);
      }
    };
    
    const handleAlbumSelect = () => {
      // Only act if an album is actually focused
      if (focusedAlbumIndex < 0) return;
      const albumCards = gridRef.current?.querySelectorAll('.album-grid-card');
      if (albumCards && albumCards[focusedAlbumIndex]) {
        console.log('🎮 Album select:', focusedAlbumIndex);
        albumCards[focusedAlbumIndex].click();
      }
    };
    
    const handleToolbarNavigate = (e) => {
      const { direction } = e.detail;
      const toolbarButtons = toolbarRef.current?.querySelectorAll('.utility-btn:not(:disabled)');
      if (!toolbarButtons || toolbarButtons.length === 0) return;
      
      // Clear album focus when navigating toolbar
      const albumCards = gridRef.current?.querySelectorAll('.album-grid-card');
      albumCards?.forEach(card => card.classList.remove('gamepad-focused'));
      setFocusedAlbumIndex(-1);
      
      // Remove focus from all toolbar buttons
      toolbarButtons.forEach(btn => btn.classList.remove('gamepad-focused'));
      
      let newIndex = focusedToolbarIndex;
      
      if (direction === 'left') {
        newIndex = newIndex > 0 ? newIndex - 1 : toolbarButtons.length - 1;
      } else if (direction === 'right') {
        newIndex = newIndex < toolbarButtons.length - 1 ? newIndex + 1 : 0;
      }
      
      // Default to first button if none focused
      if (newIndex < 0) newIndex = 0;
      
      // Apply focus
      if (toolbarButtons[newIndex]) {
        toolbarButtons[newIndex].classList.add('gamepad-focused');
        setFocusedToolbarIndex(newIndex);
        console.log('🎮 Toolbar focus:', newIndex, toolbarButtons[newIndex].textContent?.trim());
      }
    };
    
    const handleToolbarSelect = () => {
      // Only act if a toolbar button is actually focused
      if (focusedToolbarIndex < 0) return;
      const toolbarButtons = toolbarRef.current?.querySelectorAll('.utility-btn:not(:disabled)');
      if (toolbarButtons && toolbarButtons[focusedToolbarIndex]) {
        console.log('🎮 Toolbar select:', focusedToolbarIndex);
        toolbarButtons[focusedToolbarIndex].click();
      }
    };
    
    window.addEventListener('gamepadAlbumNavigate', handleAlbumNavigate);
    window.addEventListener('gamepadAlbumSelect', handleAlbumSelect);
    window.addEventListener('gamepadToolbarNavigate', handleToolbarNavigate);
    window.addEventListener('gamepadToolbarSelect', handleToolbarSelect);
    
    return () => {
      window.removeEventListener('gamepadAlbumNavigate', handleAlbumNavigate);
      window.removeEventListener('gamepadAlbumSelect', handleAlbumSelect);
      window.removeEventListener('gamepadToolbarNavigate', handleToolbarNavigate);
      window.removeEventListener('gamepadToolbarSelect', handleToolbarSelect);
    };
  }, [focusedAlbumIndex, focusedToolbarIndex]);

  // Handle album click - select album or toggle queue based on mode
  const handleAlbumClick = (album, event) => {
    if (event.shiftKey || isShiftMode) {
      // Shift mode: Add/remove album to/from playlist
      event.stopPropagation();
      const newSelected = new Set(selectedAlbums);
      if (newSelected.has(album.id)) {
        newSelected.delete(album.id);
      } else {
        newSelected.add(album.id);
      }
      setSelectedAlbums(newSelected);
    } else if (isQueueMode) {
      // Queue mode: Toggle queue status (click to add, click again to remove)
      event.stopPropagation();
      if (onAddToQueue) {
        const isCurrentlyQueued = queuedAlbumIds.has(album.id);
        onAddToQueue(album, isCurrentlyQueued); // true = remove, false = add
      }
    } else {
      // Normal mode: Select album to view details
      event.stopPropagation();
      if (onAlbumSelect) {
        onAlbumSelect(album);
      }
    }
  };

  // Clear all selected albums
  const clearPlaylist = () => {
    setSelectedAlbums(new Set());
  };

  // Save playlist (placeholder for now)
  const savePlaylist = () => {
    const playlistAlbumsList = filteredAlbums.filter(album => selectedAlbums.has(album.id));
    const artistsInPlaylist = [...new Set(playlistAlbumsList.map(a => a.artist))];
    alert(`Playlist created with ${playlistAlbumsList.length} albums from ${artistsInPlaylist.length} artists:\n\n${playlistAlbumsList.map(a => `${a.artist} - ${a.album}`).join('\n')}`);
    // TODO: Implement actual playlist save functionality
  };

  // Toggle artist expansion in Artist View mode (also adds/removes from queue)
  const handleArtistToggle = (artistName) => {
    const newExpanded = new Set(expandedArtists);
    const artistAlbums = filteredAlbums.filter(album => album.artist === artistName);
    
    if (newExpanded.has(artistName)) {
      // Collapse: remove from expanded and remove albums from queue
      newExpanded.delete(artistName);
      if (onAddToQueue) {
        artistAlbums.forEach(album => {
          if (queuedAlbumIds.has(album.id)) {
            onAddToQueue(album, true); // true = remove from queue
          }
        });
      }
    } else {
      // Expand: add to expanded and add albums to queue
      newExpanded.add(artistName);
      if (onAddToQueue) {
        artistAlbums.forEach(album => {
          if (!queuedAlbumIds.has(album.id)) {
            onAddToQueue(album, false); // false = add to queue
          }
        });
      }
    }
    setExpandedArtists(newExpanded);
  };

  // Get unique artists from filtered albums
  const getUniqueArtists = () => {
    const artistMap = new Map();
    filteredAlbums.forEach(album => {
      const artist = album.artist || 'Unknown Artist';
      if (!artistMap.has(artist)) {
        artistMap.set(artist, []);
      }
      artistMap.get(artist).push(album);
    });
    // Sort artists alphabetically
    return Array.from(artistMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  };

  // Shuffle all songs from queued albums (if any), otherwise all filtered albums
  const handleShuffleAll = () => {
    // Determine which albums to shuffle
    let albumsToShuffle;
    if (queuedAlbumIds.size > 0) {
      // If there are queued albums, only shuffle those
      albumsToShuffle = filteredAlbums.filter(album => queuedAlbumIds.has(album.id));
    } else {
      // Otherwise shuffle all filtered albums
      albumsToShuffle = filteredAlbums;
    }
    
    if (albumsToShuffle.length === 0) return;
    
    // Collect songs grouped by album for round-robin shuffling
    const songsByAlbum = new Map();
    albumsToShuffle.forEach(album => {
      if (album.songs && album.songs.length > 0) {
        // Shuffle songs within each album
        const albumSongs = album.songs.map(song => ({
          ...song,
          album: album
        })).sort(() => Math.random() - 0.5);
        songsByAlbum.set(album.id, albumSongs);
      }
    });

    // Round-robin shuffle: interleave songs from different albums
    // Never plays 2 songs in a row from the same album until all albums have had a turn
    const shuffled = [];
    const albumIds = Array.from(songsByAlbum.keys()).sort(() => Math.random() - 0.5);
    let lastAlbumId = null;
    
    while (songsByAlbum.size > 0) {
      // Get albums that still have songs
      const availableAlbums = albumIds.filter(id => songsByAlbum.has(id) && songsByAlbum.get(id).length > 0);
      
      if (availableAlbums.length === 0) break;
      
      // Try to pick a different album than the last one
      let nextAlbumId;
      if (availableAlbums.length > 1 && lastAlbumId !== null) {
        // Filter out the last album to avoid consecutive songs from same album
        const differentAlbums = [];
        for (let i = 0; i < availableAlbums.length; i++) {
          if (availableAlbums[i] !== lastAlbumId) {
            differentAlbums.push(availableAlbums[i]);
          }
        }
        if (differentAlbums.length > 0) {
          // Randomly pick from different albums
          nextAlbumId = differentAlbums[Math.floor(Math.random() * differentAlbums.length)];
        } else {
          nextAlbumId = availableAlbums[0];
        }
      } else {
        nextAlbumId = availableAlbums[Math.floor(Math.random() * availableAlbums.length)];
      }
      
      // Take one song from this album
      const albumSongs = songsByAlbum.get(nextAlbumId);
      if (albumSongs && albumSongs.length > 0) {
        shuffled.push(albumSongs.shift());
        lastAlbumId = nextAlbumId;
        
        // Remove album from map if empty
        if (albumSongs.length === 0) {
          songsByAlbum.delete(nextAlbumId);
        }
      }
    }
    
    console.log(`🔀 Round-robin shuffling ${shuffled.length} songs from ${albumsToShuffle.length} ${queuedAlbumIds.size > 0 ? 'queued' : 'filtered'} albums`);
    
    // Pass to parent component to start playback
    if (onShufflePlay && shuffled.length > 0) {
      onShufflePlay(shuffled);
    }
  };

  // Filter albums based on active filters and search query
  const getFilteredAlbums = () => {
    return albums.filter(album => {
      // Show queued only filter
      if (showQueuedOnly && !queuedAlbumIds.has(album.id)) {
        return false;
      }

      // Exclude unavailable albums (albums without files in storage) unless showUnavailable is true
      if (!showUnavailable && album.storageStatus === 'unavailable') {
        return false;
      }

      // Search filter (starts with logic)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        // For Singles, search the album name; for Albums, search the artist name
        const searchField = album.type === 'Singles' 
          ? (album.album || '').toLowerCase()
          : (album.artist || '').toLowerCase();
        
        if (!searchField.startsWith(query)) {
          return false;
        }
      }

      // Decade filter
      if (activeFilters.decade !== 'all') {
        const yearMatch = album.album?.match(/\[(\d{4})\]/);
        if (yearMatch) {
          const decade = Math.floor(parseInt(yearMatch[1]) / 10) * 10;
          if (`${decade}s` !== activeFilters.decade) return false;
        } else {
          return false;
        }
      }

      // Artist filter
      if (activeFilters.artist !== 'all' && album.artist !== activeFilters.artist) {
        return false;
      }

      return true;
    }).sort((a, b) => {
      if (sortMode === 'artist') {
        // Sort by artist first, then by album name
        const artistA = (a.artist || '').toLowerCase();
        const artistB = (b.artist || '').toLowerCase();
        const artistCompare = artistA.localeCompare(artistB);
        if (artistCompare !== 0) return artistCompare;
        // Same artist, sort by album name
        const nameA = (a.album || '').toLowerCase();
        const nameB = (b.album || '').toLowerCase();
        return nameA.localeCompare(nameB);
      } else {
        // Sort alphabetically by album name only
        const nameA = (a.album || '').toLowerCase();
        const nameB = (b.album || '').toLowerCase();
        return nameA.localeCompare(nameB);
      }
    });
  };

  const filteredAlbums = getFilteredAlbums();

  return (
    <div className="album-grid-container">
      {/* Top Filter Bar */}
      {albums.length > 0 && (
        <div className="album-grid-filters">
          {/* Desktop Utility Row - hidden on phones */}
          <div className="utility-row" ref={toolbarRef}>
            {/* Shuffle Button */}
            <button 
              className="utility-btn shuffle-btn"
              onClick={handleShuffleAll}
              disabled={filteredAlbums.length === 0}
              title={`Shuffle all albums (${filteredAlbums.length})`}
            >
              <span className="utility-icon">🔀</span>
              <span className="utility-label desktop-only">Shuffle ({filteredAlbums.length})</span>
            </button>

            {/* Search Field - Icon only, expands on click */}
            {!isSearchExpanded ? (
              <button
                className="utility-btn search-icon-btn"
                onClick={() => setIsSearchExpanded(true)}
                title="Search"
              >
                <span className="utility-icon">🔍</span>
              </button>
            ) : (
              <div className="utility-search utility-search-expanded">
                <span className="utility-icon">🔍</span>
                <input
                  type="text"
                  placeholder={selectedPath.type === 'Singles' ? 'Search album...' : 'Search artist...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="utility-search-input"
                  autoFocus
                  onBlur={() => {
                    // Collapse if search is empty
                    if (!searchQuery) {
                      setIsSearchExpanded(false);
                    }
                  }}
                />
                <button 
                  onClick={() => {
                    setSearchQuery('');
                    setIsSearchExpanded(false);
                  }}
                  className="utility-search-clear"
                  title="Clear search"
                >
                  ✕
                </button>
              </div>
            )}

            {/* Sort Button - Toggle between album and artist sorting */}
            {viewMode === 'grid' && (
              <button 
                className={`utility-btn sort-btn ${sortMode === 'artist' ? 'active' : ''}`}
                onClick={() => setSortMode(sortMode === 'album' ? 'artist' : 'album')}
                title={sortMode === 'album' ? 'Currently sorting by album name. Click to sort by artist' : 'Currently sorting by artist. Click to sort by album name'}
              >
                <span className="utility-icon">{sortMode === 'album' ? '🔤' : '👤'}</span>
                <span className="utility-label desktop-only">
                  {sortMode === 'album' ? 'Sort by Album' : 'Sort by Artist'}
                </span>
              </button>
            )}

            {/* View Mode Toggle */}
            <div className="utility-view">
              <button 
                className={`utility-btn view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => { setViewMode('grid'); setExpandedArtists(new Set()); }}
                title="Grid view"
              >
                <span className="utility-label desktop-only">Show </span>
                <span className="utility-label">Albums</span>
              </button>
              <button 
                className={`utility-btn view-btn ${viewMode === 'artist' ? 'active' : ''}`}
                onClick={() => setViewMode('artist')}
                title="Artist view"
              >
                <span className="utility-label desktop-only">Show </span>
                <span className="utility-label">Artists</span>
              </button>
            </div>

            {/* Queue Mode Toggle */}
            <button 
              className={`utility-btn queue-mode-btn ${isQueueMode ? 'active' : ''}`}
              onClick={() => {
                const newMode = !isQueueMode;
                setIsQueueMode(newMode);
                if (onQueueModeChange) onQueueModeChange(newMode);
              }}
              title={isQueueMode ? 'Queue mode ON' : 'Queue mode OFF'}
            >
              <span className="utility-icon">{isQueueMode ? '🎵' : '➕'}</span>
              <span className="utility-label desktop-only">Queue</span>
            </button>

            {/* Admin Toolbox Button - hidden */}
            {/* <button 
              className="utility-btn admin-btn"
              onClick={() => {
                alert('Opening Admin Panel...');
                window.dispatchEvent(new CustomEvent('openAdminPanel'));
              }}
              title="Admin Prompts"
            >
              <span className="utility-icon">🛠️</span>
            </button> */}
          </div>

          {/* Mobile: Compact Toolbar - all controls in one row */}
          <div className="mobile-toolbar mobile-only">
            {/* Shuffle */}
            <button 
              className="mobile-btn shuffle"
              onClick={handleShuffleAll}
              disabled={filteredAlbums.length === 0}
              title={`Shuffle ${filteredAlbums.length} albums`}
            >
              🔀 <span className="mobile-btn-count">{filteredAlbums.length}</span>
            </button>

            {/* Sort (only in grid view) */}
            {viewMode === 'grid' && (
              <>
                <button 
                  className={`mobile-btn ${sortMode === 'album' ? 'active' : ''}`}
                  onClick={() => setSortMode('album')}
                  title="Sort A-Z"
                >
                  🔤
                </button>
                <button 
                  className={`mobile-btn ${sortMode === 'artist' ? 'active' : ''}`}
                  onClick={() => setSortMode('artist')}
                  title="Sort by Artist"
                >
                  👤
                </button>
              </>
            )}

            {/* View Toggle */}
            <button 
              className={`mobile-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => { setViewMode('grid'); setExpandedArtists(new Set()); }}
              title="Grid view"
            >
              ▦
            </button>
            {/* Artist view button hidden by default */}
            {/* <button 
              className={`mobile-btn ${viewMode === 'artist' ? 'active' : ''}`}
              onClick={() => setViewMode('artist')}
              title="Artist view"
            >
              👥
            </button> */}

            {/* Queue Mode */}
            <button 
              className={`mobile-btn queue ${isQueueMode ? 'active' : ''}`}
              onClick={() => {
                const newMode = !isQueueMode;
                setIsQueueMode(newMode);
                if (onQueueModeChange) onQueueModeChange(newMode);
              }}
              title={isQueueMode ? 'Queue ON' : 'Queue OFF'}
            >
              {isQueueMode ? '🎵' : '➕'}
            </button>

            {/* Search */}
            <div className="mobile-search">
              <input
                type="text"
                placeholder="🔍 Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="mobile-search-clear">✕</button>
              )}
            </div>
          </div>

          {/* Shift Mode Indicator */}
          {isShiftMode && (
            <div className="shift-mode-indicator">
              ⇧ SHIFT MODE: Click albums to add to playlist
            </div>
          )}

          {/* Playlist Controls */}
          {selectedAlbums.size > 0 && (
            <div className="playlist-controls">
              <span className="playlist-count">
                📋 Playlist: {selectedAlbums.size} album{selectedAlbums.size !== 1 ? 's' : ''} selected
              </span>
              <button onClick={savePlaylist} className="save-playlist-btn">
                💾 Save Playlist
              </button>
              <button onClick={clearPlaylist} className="clear-playlist-btn">
                ✕ Clear
              </button>
            </div>
          )}

          {/* Artists filter - hidden by default */}
          {/* {viewMode === 'grid' && filters.artists.length > 1 && (
            <div className="filter-group artists-filter-group">
              <div className="filter-buttons">
                <button 
                  className={activeFilters.artist === 'all' ? 'active' : ''}
                  onClick={() => setActiveFilters({...activeFilters, artist: 'all'})}
                >
                  All
                </button>
              {filters.artists.map(artist => (
                <button
                  key={artist}
                  className={activeFilters.artist === artist ? 'active' : ''}
                  onClick={() => setActiveFilters({...activeFilters, artist})}
                >
                  {artist}
                </button>
              ))}
              </div>
            </div>
          )} */}

          <div className="filter-results">
            Showing {filteredAlbums.length} of {albums.length} albums
          </div>
        </div>
      )}

      {/* Album Grid / Artist View */}
      {filteredAlbums.length > 0 && (
        <div className={`album-grid-content ${mediaPlayerVisible ? 'player-visible' : ''}`}>
          {loading ? (
            <div className="album-grid-loading">
              <p>Loading albums...</p>
            </div>
          ) : viewMode === 'artist' ? (
          /* Artist View Mode */
          <div className="artist-view">
            {/* Selected Artists Summary Bar */}
            {expandedArtists.size > 0 && (
              <div className="selected-artists-bar">
                <span className="selected-artists-label">Selected ({expandedArtists.size}):</span>
                <div className="selected-artists-chips">
                  {Array.from(expandedArtists).sort().map(artistName => (
                    <span 
                      key={artistName} 
                      className="selected-artist-chip"
                      onClick={() => handleArtistToggle(artistName)}
                      title="Click to remove"
                    >
                      {artistName}
                      <span className="chip-remove">✕</span>
                    </span>
                  ))}
                </div>
                <button 
                  className="clear-all-artists-btn"
                  onClick={() => {
                    // Remove all artists from queue
                    expandedArtists.forEach(artistName => {
                      handleArtistToggle(artistName);
                    });
                  }}
                  title="Clear all selected artists"
                >
                  Clear All
                </button>
              </div>
            )}
            {getUniqueArtists().map(([artistName, artistAlbums]) => (
              <div 
                key={artistName} 
                className={`artist-section ${expandedArtists.has(artistName) ? 'expanded' : ''}`}
              >
                <div 
                  className="artist-header"
                  onClick={() => handleArtistToggle(artistName)}
                >
                  <span className="artist-expand-icon">
                    {expandedArtists.has(artistName) ? '▼' : '▶'}
                  </span>
                  <span className="artist-name">{artistName}</span>
                  <span className="artist-album-count">
                    {artistAlbums.length} album{artistAlbums.length !== 1 ? 's' : ''}
                  </span>
                  {expandedArtists.has(artistName) && (
                    <span className="artist-queued-badge">✓ Queued</span>
                  )}
                </div>
                {expandedArtists.has(artistName) && (
                  <div className="artist-albums">
                    {artistAlbums.map(album => (
                      <AlbumCard
                        key={album.id}
                        album={album}
                        isSelected={selectedAlbum?.id === album.id}
                        isInPlaylist={selectedAlbums.has(album.id)}
                        isQueued={queuedAlbumIds.has(album.id)}
                        onSelect={handleAlbumClick}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          /* Grid View Mode */
          <div className="grid-view-container">
            {/* Selected Albums Summary Bar */}
            {queuedAlbumIds.size > 0 && (
              <div className="selected-albums-bar">
                <span className="selected-albums-label">Queued ({queuedAlbumIds.size}):</span>
                <div className="selected-albums-chips">
                  {filteredAlbums
                    .filter(album => queuedAlbumIds.has(album.id))
                    .map(album => (
                      <span 
                        key={album.id} 
                        className="selected-album-chip"
                        onClick={() => onAddToQueue && onAddToQueue(album, true)}
                        title="Click to remove from queue"
                      >
                        {album.album || 'Unknown'}
                        <span className="chip-remove">✕</span>
                      </span>
                    ))}
                </div>
                <div className="query-buttons-group">
                  {onQueryMusicbrainz && (
                    <button
                      className="query-musicbrainz-btn"
                      onClick={() => {
                        const queuedAlbums = filteredAlbums.filter(album => queuedAlbumIds.has(album.id));
                        onQueryMusicbrainz(queuedAlbums, selectedPath);
                      }}
                      title="Query Musicbrainz to populate DisplayArtist, DisplayAlbum, DisplayTitle, Year, and Length"
                    >
                      🎵 Musicbrainz
                    </button>
                  )}
                  {onQuerySpotify && (
                    <button
                      className="query-spotify-btn"
                      onClick={() => {
                        const queuedAlbums = filteredAlbums.filter(album => queuedAlbumIds.has(album.id));
                        onQuerySpotify(queuedAlbums, selectedPath);
                      }}
                      title="Query Spotify to find album metadata and track information. ⚠️ Make sure you are authenticated with Spotify first (go to /services page to connect)"
                    >
                      🟢 Spotify
                    </button>
                  )}
                  {onQueryLastfm && (
                    <button
                      className="query-lastfm-btn"
                      onClick={() => {
                        const queuedAlbums = filteredAlbums.filter(album => queuedAlbumIds.has(album.id));
                        onQueryLastfm(queuedAlbums, selectedPath);
                      }}
                      title="Query Last.fm to find album metadata and track information"
                    >
                      🎧 Last.fm
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="album-grid" ref={gridRef}>
              {filteredAlbums.map(album => (
                <AlbumCard
                  key={album.id}
                  album={album}
                  isSelected={selectedAlbum?.id === album.id}
                  isInPlaylist={selectedAlbums.has(album.id)}
                  isQueued={queuedAlbumIds.has(album.id)}
                  onSelect={handleAlbumClick}
                />
              ))}
            </div>
          </div>
        )}
        </div>
      )}
    </div>
  );
}

export default AlbumGrid;


